import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../config/supabaseClient';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';
import ReliabilityBar from '../components/ui/ReliabilityBar';
import RatingModal from '../components/RatingModal';
import MatchHistory from '../components/MatchHistory';
import EloChart from '../components/EloChart';
import './Dashboard.css';

/* ─────────────────────────────────────────────────────────────
   DASHBOARD — shows the logged-in player's ELO rating and stats.

   On load it fetches the player's profile from Supabase. If no
   profile exists yet (old test accounts), it creates one on the
   fly with default values so the user never sees an error.
   ───────────────────────────────────────────────────────────── */
function Dashboard() {
  const { user } = useAuth();
  const [profile, setProfile]     = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [matchHistoryKey, setMatchHistoryKey] = useState(0);
  const [statsMode, setStatsMode]             = useState('singles'); // 'singles' | 'doubles'

  // Opens the app-level ReportMatchModal via window event
  function openReportMatch(opts = {}) {
    window.dispatchEvent(new CustomEvent('ranktt:openReportMatch', {
      detail: {
        ...opts,
        userRating:  profile?.elo_rating,
        userMatches: profile?.matches_played,
      },
    }));
  }

  useEffect(() => {
    if (user) fetchOrCreateProfile();
  }, [user]);

  // Re-fetch profile + refresh history when the user confirms or declines a
  // match notification in the same tab.
  useEffect(() => {
    function onMatchResolved() {
      fetchOrCreateProfile();
      setMatchHistoryKey(k => k + 1);
    }
    window.addEventListener('ranktt:matchResolved', onMatchResolved);
    return () => window.removeEventListener('ranktt:matchResolved', onMatchResolved);
  }, []);

  async function fetchOrCreateProfile() {
    setLoading(true);
    setError('');

    // Try to fetch the existing profile
    let { data, error: fetchError } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();

    // PGRST116 means no row found — create the profile on the fly
    if (fetchError && fetchError.code === 'PGRST116') {
      const { data: newProfile, error: insertError } = await supabase
        .from('profiles')
        .insert({
          id:         user.id,
          first_name: user.user_metadata?.first_name || null,
          last_name:  user.user_metadata?.last_name  || null,
          full_name:  user.user_metadata?.full_name  || null,
          username:   user.user_metadata?.username   || null,
        })
        .select()
        .single();

      if (insertError) {
        setError('Could not load your profile. Please refresh and try again.');
        setLoading(false);
        return;
      }

      data = newProfile;
    } else if (fetchError) {
      setError('Could not load your profile. Please refresh and try again.');
      setLoading(false);
      return;
    }

    // The database trigger creates the profile row instantly on sign-up but
    // leaves name/username null. If we have those values in auth metadata
    // and the profile row is still blank, backfill them now.
    const meta = user.user_metadata || {};
    if (data && !data.first_name && !data.username && meta.username) {
      const { data: updatedProfile } = await supabase
        .from('profiles')
        .update({
          first_name: meta.first_name || null,
          last_name:  meta.last_name  || null,
          full_name:  meta.full_name  || null,
          username:   meta.username   || null,
        })
        .eq('id', user.id)
        .select()
        .single();

      if (updatedProfile) data = updatedProfile;
    }

    setProfile(data);
    setLoading(false);
  }

  // Win rates
  const totalSingles = profile ? profile.wins + profile.losses : 0;
  const singlesWinRate = totalSingles > 0 ? Math.round((profile.wins / totalSingles) * 100) + '%' : '—';
  const totalDoubles = profile ? (profile.doubles_wins ?? 0) + (profile.doubles_losses ?? 0) : 0;
  const doublesWinRate = totalDoubles > 0 ? Math.round(((profile.doubles_wins ?? 0) / totalDoubles) * 100) + '%' : '—';
  const winRate = statsMode === 'doubles' ? doublesWinRate : singlesWinRate;

  // First name for the greeting — use stored field, fall back gracefully
  const firstName = profile?.first_name
    || user?.user_metadata?.first_name
    || 'Player';

  return (
    <div className="dashboard">
      <div className="dashboard-inner">

        {/* Loading state */}
        {loading && (
          <div className="dashboard-loading">
            <span className="dashboard-loading-star">★</span>
            <span className="dashboard-loading-star">★</span>
            <span className="dashboard-loading-star">★</span>
          </div>
        )}

        {/* Error state */}
        {!loading && error && (
          <div className="dashboard-error">
            <p>{error}</p>
          </div>
        )}

        {/* Main content — only shown once profile is loaded */}
        {!loading && !error && profile && (
          <>
            {/* Page header — inside the profile guard so name never flashes as "Player" */}
            <div className="dashboard-header">
              <h1 className="dashboard-welcome">Welcome back, {firstName}</h1>
              <p className="dashboard-subtitle">Your RankTT stats</p>
            </div>
            {/* Singles / Doubles tab switcher — only shown when rated */}
            {profile.is_rated && (
              <div className="dashboard-mode-toggle">
                <button
                  className={`dashboard-mode-btn ${statsMode === 'singles' ? 'dashboard-mode-btn--active' : ''}`}
                  onClick={() => setStatsMode('singles')}
                >
                  Singles
                </button>
                <button
                  className={`dashboard-mode-btn ${statsMode === 'doubles' ? 'dashboard-mode-btn--active' : ''}`}
                  onClick={() => setStatsMode('doubles')}
                >
                  Doubles
                </button>
              </div>
            )}

            {/* ELO hero card */}
            {!profile.is_rated ? (
              <Card className="elo-card elo-card-unrated" highlighted>
                <span className="elo-label">Unrated</span>
                <p className="elo-unrated-sub">
                  Get an estimated starting rating tailored to your skill level.
                </p>
                <button className="elo-get-rated-btn" onClick={() => setRatingModalOpen(true)}>
                  Get your estimated rating →
                </button>
              </Card>
            ) : statsMode === 'doubles' ? (
              <Card className="elo-card" highlighted>
                <span className="elo-label">Doubles Rating</span>
                <span className="elo-number">{profile.doubles_elo ?? profile.elo_rating}</span>
                <span className="elo-sublabel">Doubles ELO</span>
                <ReliabilityBar
                  matchesPlayed={profile.doubles_matches_played ?? 0}
                  uniqueOpponents={profile.doubles_unique_opponents_count ?? 0}
                />
              </Card>
            ) : (
              <Card className="elo-card" highlighted>
                <span className="elo-label">Current Rating</span>
                <span className="elo-number">{profile.elo_rating}</span>
                <span className="elo-sublabel">ELO Rating</span>
                <ReliabilityBar
                  matchesPlayed={profile.matches_played ?? 0}
                  uniqueOpponents={profile.unique_opponents_count ?? 0}
                />
              </Card>
            )}

            {/* Wins / Losses / Win Rate */}
            <div className="stats-grid">
              <Card className="stat-card">
                <span className="stat-value">{statsMode === 'doubles' ? (profile.doubles_wins ?? 0) : profile.wins}</span>
                <span className="stat-label">Wins</span>
              </Card>
              <Card className="stat-card">
                <span className="stat-value">{statsMode === 'doubles' ? (profile.doubles_losses ?? 0) : profile.losses}</span>
                <span className="stat-label">Losses</span>
              </Card>
              <Card className="stat-card">
                <span className="stat-value">{winRate}</span>
                <span className="stat-label">Win Rate</span>
              </Card>
            </div>

            {/* Report a Match button */}
            <div className="dashboard-actions">
              <Button size="lg" onClick={() => openReportMatch()}>
                + Report a Match
              </Button>
            </div>

            {/* ELO history graph */}
            <div className="dashboard-section">
              <h2 className="dashboard-section-title">Rating History</h2>
              <EloChart userId={profile.id} refreshKey={matchHistoryKey} createdAt={profile.created_at} showStatsLink isDoubles={statsMode === 'doubles'} />
            </div>

            {/* Recent matches */}
            <div className="dashboard-section">
              <h2 className="dashboard-section-title">Recent Matches</h2>
              <MatchHistory
                key={matchHistoryKey}
                userId={profile.id}
                currentUserId={profile.id}
                limit={5}
                viewMoreHref={`/match-history/${profile.username}`}
                onReportMatch={() => openReportMatch()}
                onEditMatch={(match) => openReportMatch({ editMatch: match })}
              />
            </div>
          </>
        )}

      </div>

      {/* Rating survey modal — shown to new users who haven't been rated yet */}
      <RatingModal
        isOpen={ratingModalOpen}
        onClose={() => setRatingModalOpen(false)}
        userId={user?.id}
        onRated={() => {
          setRatingModalOpen(false);
          fetchOrCreateProfile();
        }}
      />

    </div>
  );
}


export default Dashboard;
